import React, { useRef, useEffect, useCallback, useImperativeHandle } from 'react';

/**
 * Lienzo de firma manuscrita en coordenadas CSS (ctx escalado por devicePixelRatio).
 * ref: { reset(), toDataURL() }
 */
const ConsentimientoFirmaPad = React.forwardRef(function ConsentimientoFirmaPad({ disabled, className = '' }, ref) {
  const canvasRef = useRef(null);

  const setupCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) return;
    const wCss = Math.max(260, wrap.clientWidth - 8);
    const hCss = 168;
    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    canvas.style.width = `${wCss}px`;
    canvas.style.height = `${hCss}px`;
    canvas.width = Math.round(wCss * dpr);
    canvas.height = Math.round(hCss * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, wCss, hCss);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    setupCanvasSize();
    const ro = new ResizeObserver(() => setupCanvasSize());
    const wrap = canvasRef.current?.parentElement;
    if (wrap) ro.observe(wrap);
    const onResize = () => setupCanvasSize();
    window.addEventListener('resize', onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [setupCanvasSize]);

  useImperativeHandle(ref, () => ({
    reset: () => setupCanvasSize(),
    toDataURL: () => canvasRef.current?.toDataURL('image/png') || '',
    hasInk: () => {
      const canvas = canvasRef.current;
      if (!canvas || canvas.width === 0) return false;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;
      const { width, height } = canvas;
      const { data } = ctx.getImageData(0, 0, width, height);
      for (let i = 0; i < data.length; i += 32) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        if (r < 250 || g < 250 || b < 250) return true;
      }
      return false;
    }
  }), [setupCanvasSize]);

  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };

  const drawLine = (x0, y0, x1, y1) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  };

  const handleStart = (e) => {
    if (disabled) return;
    e.preventDefault();
    const p = getPos(e);
    if (!p) return;
    drawing.current = true;
    last.current = p;
  };

  const handleMove = (e) => {
    if (disabled || !drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    if (!p) return;
    drawLine(last.current.x, last.current.y, p.x, p.y);
    last.current = p;
  };

  const handleEnd = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
  };

  return (
    <div className={`consentimiento-firma-pad rounded border bg-white overflow-hidden ${className}`}>
      <canvas
        ref={canvasRef}
        role="presentation"
        aria-hidden
        className="consentimiento-firma-canvas d-block"
        style={{ verticalAlign: 'top', cursor: disabled ? 'not-allowed' : 'crosshair', touchAction: 'none' }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
    </div>
  );
});

export default ConsentimientoFirmaPad;
